import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiClock, FiInbox, FiPackage } = FiIcons;

export default function MyBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookings = async () => {
      // Fetch bookings with their associated services in one go
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          booking_services(
            id,
            quantity,
            services(name)
          )
        `)
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false });

      if (data) setBookings(data);
      setLoading(false);
    };
    fetchBookings();
  }, [user]);

  const getStatusColor = (status) => {
    const map = {
      pending_assignment: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      driver_accepted: 'bg-blue-100 text-blue-800',
      driver_on_the_way: 'bg-orange-100 text-orange-800',
      driver_reached_pickup: 'bg-purple-100 text-purple-800',
      pickup_started: 'bg-teal-100 text-teal-800',
      pickup_completed: 'bg-green-100 text-green-800',
      processing: 'bg-indigo-100 text-indigo-800',
      ready_for_delivery: 'bg-orange-100 text-orange-800',
      out_for_delivery: 'bg-teal-100 text-teal-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status) => status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <div className="p-4 pt-8 pb-24">
      <h1 className="text-2xl font-bold text-brand-navy mb-6">My Bookings</h1>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => <div key={i} className="h-40 bg-gray-200 animate-pulse rounded-2xl w-full"></div>)}
        </div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-12 text-gray-500 flex flex-col items-center">
          <FiInbox className="text-4xl mb-4 opacity-50" />
          <p>No bookings found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map(booking => (
            <Card key={booking.id}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-xs text-gray-400 font-mono mb-1">{booking.booking_number}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${getStatusColor(booking.status)}`}>
                    {formatStatus(booking.status)}
                  </span>
                </div>
                <div className="flex flex-col items-end">
                   <div className="flex items-center gap-1 text-xs text-brand-navy font-semibold bg-brand-light px-2 py-1 rounded-lg">
                     <FiPackage className="text-brand-gold" />
                     {booking.booking_services?.reduce((acc, s) => acc + s.quantity, 0)} Items
                   </div>
                </div>
              </div>

              {/* Service Breakdown */}
              <div className="space-y-1.5 mb-4">
                {booking.booking_services?.map((bs) => (
                  <div key={bs.id} className="flex justify-between text-sm">
                    <span className="text-gray-500">{bs.services?.name}</span>
                    <span className="text-brand-navy font-bold">{bs.quantity}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-50 pt-3 flex gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <FiCalendar className="text-brand-gold" />
                  {booking.pickup_date}
                </div>
                <div className="flex items-center gap-1">
                  <FiClock className="text-brand-gold" />
                  {booking.pickup_slot.split(' - ')[0]}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}